import { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const DISMISS_KEY = "dp_pwa_banner_dismissed_v2";
const INSTALLED_KEY = "dp_pwa_installed_v2";

const detectPlatform = () => {
  if (typeof window === "undefined") return "other";
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (isStandalone) return "installed";
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "desktop";
};

const PWAInstallBanner = () => {
  const [platform, setPlatform] = useState("other");
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    
    const dismissed = localStorage.getItem(DISMISS_KEY);
    const installed = localStorage.getItem(INSTALLED_KEY);
    
    // Show only if not installed, not dismissed, and on mobile
    if (p !== "installed" && !dismissed && !installed) {
      setVisible(true);
    }
    
    const onBefore = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, "1"); } catch { /* ignore */ }
      setDeferredPrompt(null);
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch { /* localStorage full */ }
  };

  const handleNativeInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch { /* dismissed */ }
      setDeferredPrompt(null);
      setDialogOpen(false);
    } else {
      setDialogOpen(true);
    }
  };

  if (!visible || platform === "installed") return null;

  return (
    <>
      <div
        className="sticky top-0 z-40 bg-gradient-to-r from-emerald-900 to-emerald-950 dark:from-emerald-950 dark:to-black text-white shadow-md"
        data-testid="pwa-install-banner"
      >
        <div className="max-w-6xl mx-auto px-3 py-2 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">Namesti Dober pogled app</p>
            <p className="text-[11px] text-white/80 leading-tight">Hitrejši dostop, potisna sporočila, deluje brez brskalnika</p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="h-8 gap-1 flex-shrink-0"
            onClick={handleNativeInstall}
            data-testid="pwa-banner-install-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Namesti
          </Button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Zapri"
            className="text-white/70 hover:text-white p-1 flex-shrink-0"
            data-testid="pwa-banner-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Fallback dialog with iOS/Android instructions */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              Namesti Dober pogled app
            </DialogTitle>
            <DialogDescription>Dodaj aplikacijo na začetni zaslon telefona.</DialogDescription>
          </DialogHeader>
          {platform === "ios" && (
            <div className="text-sm space-y-2">
              <p><strong>Safari na iPhone/iPad:</strong></p>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>Tapni gumb <strong>Deli</strong> spodaj v Safariju</li>
                <li>Scroll → <strong>&quot;Dodaj na začetni zaslon&quot;</strong></li>
                <li>Pritisni <strong>Dodaj</strong> zgoraj desno</li>
              </ol>
            </div>
          )}
          {platform === "android" && (
            <div className="text-sm space-y-2">
              <p><strong>Chrome/Samsung Browser:</strong></p>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>Tapni <strong>⋮ Meni</strong> zgoraj desno</li>
                <li>Izberi <strong>&quot;Namesti aplikacijo&quot;</strong></li>
                <li>Potrdi z <strong>Namesti</strong></li>
              </ol>
            </div>
          )}
          {platform === "desktop" && (
            <div className="text-sm">
              V naslovni vrstici Chrome/Edge pritisni ikono <Download className="h-4 w-4 inline" /> ali <strong>⋮ Meni → Namesti Dober pogled app</strong>.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PWAInstallBanner;
